/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import ShortNameField from '../Common/shortNameField';
import DetailStatus from './detailStatus';
import DetailHeader from './detailHeader';
import PlanTab from './detailPlanTab';
import TargetBurnup from '../Graph/targetBurnup';
import { fetchUsers } from '../../actions/userActions';
import { openModal } from '../../actions/modalActions';
import {
  createTarget,
  deleteTarget,
  updateTarget,
  targetBurnup,
  restoreTarget
} from '../../actions/targetActions';
import { getAllCadences } from '../../actions/cadenceActions';
import { getAllProducts } from '../../actions/productActions';
import { targetWorkPackagesSelector } from '../../selectors/workPackageSelector';
import {
  addWorkPackageToTargetWithUnfinishedDescendantsAndAncestors,
  removeWorkPackageFromTarget
} from '../../actions/workPackageActions';
import { allows } from '../../utils/authorization';
import SelectField from '../Common/selectField';
import {
  unfinishedDescendantsAndAncestorsNotInTarget,
  extractAllDescendants
} from '../../utils/hierarchyHelpers';
import divider from '../../../assets/images/large-card-nav-divider.png';

class TargetDetail extends React.Component {
  constructor(props) {
    super(props);
    const { target = {}, productList, modalState } = props;
    const productId = productList.length > 0 ? productList[0].id : '';
    this.state = {
      disableBtn: false,
      dropDown: {},
      isFormValid: true,
      editName: true,
      activeTab: 'plan',
      target: modalState.target || {
        // use target from saved modal state if available
        ...target,
        id: target.id || 0,
        short_name: target.short_name || '',
        start_date: target.start_date || moment().format('YYYY-MM-DD'),
        end_date: target.end_date || '',
        product_id: target.product_id || productId,
        workPackageIds: []
      }
    };
  }

  componentDidUpdate(prevProps) {
    const { target } = this.props;
    if (prevProps.target && prevProps.target.status !== target.status) {
      const newTarget = cloneDeep(target);
      newTarget.status = target.status;
      this.setState({ target: newTarget });
    }
  }

  setDropdown = (updatedDropdDown) => {
    this.setState({ dropDown: updatedDropdDown });
  };

  handleFieldChange = (field) => {
    const { target } = this.state;
    const newTarget = cloneDeep(target);

    if (field.end_date) newTarget.end_date = field.end_date;
    else if (field.start_date) newTarget.start_date = field.start_date;
    else newTarget.short_name = field.short_name;
    this.setState({ target: newTarget, hasChanged: !target.id }, () => {
      if (target.id) {
        if (!target.short_name || !target.end_date || !target.start_date) {
          this.setState({ isFormValid: false });
        } else {
          const value = field.target
            ? { [field.target.name]: field.target.value }
            : field;
          this.props.updateTarget(this.props.target.id, value);
        }
      } else this.setState({ isFormValid: true });
    });
  };

  handleSave = (event) => {
    if (event) event.preventDefault();
    const { target } = this.state;

    if (!target.short_name || !target.end_date || !target.start_date) {
      return this.setState({ isFormValid: false });
    }
    this.setState({ disableBtn: true });
    this.props.createTarget(target).then(() => this.props.onClose());
    return null;
  };

  handleSelectChange = (value, name) => {
    const { target } = this.state;
    const newTarget = cloneDeep(target);
    newTarget[name] = value;
    this.setState({ target: newTarget });
    if (target.id) this.props.updateTarget(target.id, { [name]: value });
  };

  handleUnfinishedChildrenSubmit = (resultIds) => {
    const newTarget = cloneDeep(this.state.target);
    newTarget.workPackageIds = newTarget.workPackageIds.concat(resultIds);
    this.setState({ target: newTarget });
  };

  handleAddWorkPackageToTarget = (wpId) => {
    const { target } = this.state;
    const { allWorkPackages } = this.props;

    if (target.id)
      this.props.addWorkPackageToTargetWithUnfinishedDescendantsAndAncestors(
        wpId,
        target.id
      );
    else {
      const newTarget = cloneDeep(target);

      // disabled due to bug affecting new target modals getting new instance when a child modal is opened.
      // same reason close button is removed.

      // if (
      //   (!!currentWorkPackage.target_id && descendants.length > 0) ||
      //   descendants.some(wp => !!wp.target_id)
      // ) {
      //   return this.props.openModal(modalTypes.unfinishedChildren, {
      //     isOpen: true,
      //     parentWorkPackage: currentWorkPackage,
      //     unfinishedResults: descendants,
      //     isNewTarget: true,
      //     handleConfirmation: this.handleUnfinishedChildrenSubmit,
      //     target
      //   });
      // }
      const currentWorkPackage = allWorkPackages.find((wp) => wp.id === wpId);
      const descendantsAndAncestors = unfinishedDescendantsAndAncestorsNotInTarget(
        currentWorkPackage,
        allWorkPackages, // TODO -- only include descendants that are in the source target already. Otherwise will add all descendants of a higher level result to the target, even if they are not in the source
        target
      );

      const idsToAdd = descendantsAndAncestors.map((wp) => wp.id);
      if (
        !newTarget.workPackageIds.find((wp) => wp.id === currentWorkPackage.id)
      )
        idsToAdd.push(currentWorkPackage.id);

      newTarget.workPackageIds = newTarget.workPackageIds.concat(idsToAdd);

      this.setState({ target: newTarget });
      this.props.saveModalState({ target: newTarget }); // preserve against unmounting when child modal opened
    }
  };

  handleRemoveWorkPackageFromTarget = (wpId) => {
    const { target } = this.state;
    if (target.id) this.props.removeWorkPackageFromTarget(wpId, target.id);
    else {
      const newTarget = cloneDeep(target);
      const descendantIds = extractAllDescendants(
        [wpId],
        this.props.allWorkPackages
      ).map((wp) => wp.id);
      descendantIds.push(wpId);
      newTarget.workPackageIds = newTarget.workPackageIds.filter(
        (id) => !descendantIds.includes(id)
      );
      this.setState({ target: newTarget });
      this.props.saveModalState({ target: newTarget }); // preserve against unmounting when child modal opened
    }
  };

  closeForm = () => {
    // const { hasChanged } = this.state;

    // if (hasChanged) {
    //   const message = 'Do you want to save the changes you made in this form?';
    //   const actions = {
    //     save: {
    //       name: 'Save',
    //       function: this.handleSave
    //     },
    //     dontSave: {
    //       name: "Don't save",
    //       function: this.props.onClose
    //     }
    //   };
    //   this.props.openModal(
    //     modalTypes.message,
    //     {
    //       message,
    //       actions,
    //       closeButtonTitle: 'Cancel'
    //     },
    //     'confirm'
    //   );
    // } else {
    this.props.onClose();
    // }
  };

  openNameEdit = () => {
    if (this.state.editName) return;
    this.setState({ editName: true });
  };

  closeNameEdit = () => {
    if (!this.state.target.short_name) return;
    this.setState({ editName: false });
  };

  renderTabLink = (name) => {
    const { target } = this.state;
    if (target.id) {
      return (
        <a href={`#${name}`} onClick={() => this.setActiveTab(name)}>
          {name.toUpperCase()}
        </a>
      );
    }
    return <a>{name.toUpperCase()}</a>;
  };

  setActiveTab = (tabName) => {
    this.setState({ activeTab: tabName });
  };

  generateTabClass = (tabName) =>
    tabName === this.state.activeTab ? 'active' : '';

  renderTabs = (target, targetWorkPackages, dropDown) => {
    switch (this.state.activeTab) {
      case 'plan':
        return (
          <div role="tabpanel" id="details">
            <div id="target-detail" className="target-plan">
              <PlanTab
                target={target}
                targetWorkPackages={targetWorkPackages}
                dropDown={dropDown}
                setDropdown={this.setDropdown}
                add={this.handleAddWorkPackageToTarget}
                remove={this.handleRemoveWorkPackageFromTarget}
              />
            </div>
          </div>
        );
      case 'scope':
        return (
          <div role="tabpanel" id="stories">
            YET TO BE IMPLEMENTED
          </div>
        );
      case 'teams':
        return (
          <div role="tabpanel" id="teams">
            YET TO BE IMPLEMENTED
          </div>
        );
      case 'metrics':
        return (
          <div role="tabpanel" id="metrics">
            <TargetBurnup
              targetId={target.id}
              isLoadingData={this.props.isFetching}
              loadGraph={this.props.targetBurnup}
            />
          </div>
        );
    }
  };

  render() {
    if (!this.props.isOpen) return null;
    const { target, disableBtn, isFormValid, editName } = this.state;
    const { targetWorkPackages, productList } = this.props;
    const { dropDown } = this.state;
    const allowEdit = target.id > 0 ? allows('update', target) : true;

    const productOptions = productList.map((product) => ({
      label: product.short_name,
      value: product.id
    }));

    const selectedProduct = productOptions.find(
      (option) => option.value === target.product_id
    ) || { label: '', value: null };

    return (
      <div
        className="detail-modal workpackage-detail target-detail cadence-detail"
        tabIndex="-1"
        role="dialog"
      >
        <div className="detail-dialog" role="document">
          <div className="detail-content">
            <div className="modal-header detail-header">
              <DetailHeader
                target={target}
                stories={targetWorkPackages}
                onClose={this.closeForm}
                handleUpdate={this.handleFieldChange}
                handleDelete={this.props.deleteTarget}
                handleRestore={this.props.restoreTarget}
                isValid={isFormValid}
              />
            </div>
            <div className="modal-body target-modal-body">
              <div
                className="target-name-container"
                onClick={this.openNameEdit}
              >
                {editName ? (
                  <ShortNameField
                    shortName={target.short_name}
                    onNameChange={this.handleFieldChange}
                    allowEdit={allowEdit}
                    isValid={isFormValid}
                    autoFocus
                    unFocus={this.closeNameEdit}
                    placeholder="Target Name"
                  />
                ) : (
                  `${target.short_name || ''}`
                )}
              </div>
              <ul className="nav nav-tabs" role="tablist">
                <li
                  role="presentation"
                  className={this.generateTabClass('plan')}
                >
                  {this.renderTabLink('plan')} <img src={divider} alt="" />
                </li>
                {/* <li role="presentation">
                  {this.renderTabLink('scope')} <img src={divider} alt="" />
                </li> */}
                <li
                  role="presentation"
                  className={this.generateTabClass('metrics')}
                >
                  {this.renderTabLink('metrics')} <img src={divider} alt="" />
                </li>
                {/* <li role="presentation">{this.renderTabLink('teams')} </li> */}
              </ul>

              <div className="tab-content">
                {this.renderTabs(target, targetWorkPackages, dropDown)}
              </div>
            </div>
            <div className="modal-footer">
              <div className="row footer-selects">
                {!target.id && (
                  <div className="col-md-3 buttons-container">
                    <div className="save-cancel-buttons">
                      <label>&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-md button-lavender"
                        disabled={disableBtn}
                        onClick={this.handleSave}
                      >
                        <strong>save</strong>
                      </button>
                      <button
                        type="button"
                        className="btn btn-md btn-default button-normal"
                        onClick={this.closeForm}
                      >
                        <strong>Cancel</strong>
                      </button>
                    </div>
                  </div>
                )}
                <div className="col-md-3">
                  <SelectField
                    label="Product"
                    value={selectedProduct}
                    options={productOptions}
                    allowEdit={allowEdit}
                    handleChange={this.handleSelectChange}
                    placeholder="Select Product"
                    labelClass=""
                    searchContainerClass=""
                    clearable={false}
                  />
                </div>
              </div>
              {target.id ? (
                <DetailStatus
                  targetId={target.id}
                  status={target.status}
                  target={target}
                  targetWorkPackages={targetWorkPackages}
                  allowEdit={allowEdit}
                />
              ) : null}
            </div>
          </div>
        </div>
        {/*        {dropDown &&
          dropDown.id && (
            <DropDownMenu
              type={dropDown.type}
              dropDown={dropDown}
              key={dropDown.id}
            />
          )} */}
      </div>
    );
  }
}

TargetDetail.propTypes = {
  id: PropTypes.number,
  target: PropTypes.object,
  targetWorkPackages: PropTypes.array,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fetchUsers: PropTypes.func,
  updateTarget: PropTypes.func,
  deleteTarget: PropTypes.func,
  getAllCadences: PropTypes.func,
  addWorkPackagesToTarget: PropTypes.func,
  removeWorkPackageFromTarget: PropTypes.func,
  allWorkPackages: PropTypes.array
};

const mapStateToProps = (state, ownProps) => {
  const target = state.targetReducer.targets.find((t) => t.id === ownProps.id);
  const targetWorkPackages = targetWorkPackagesSelector(state);

  return {
    target,
    userList: state.userReducer,
    targetWorkPackages: targetWorkPackages[ownProps.id] || [],
    productList: state.productReducer.products,
    isFetching: state.targetReducer.isFetching,
    allWorkPackages: state.workPackageReducer.all
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      openModal,
      addWorkPackageToTargetWithUnfinishedDescendantsAndAncestors,
      removeWorkPackageFromTarget,
      getAllProducts,
      createTarget,
      getAllCadences,
      deleteTarget,
      updateTarget,
      restoreTarget,
      fetchUsers,
      targetBurnup
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(TargetDetail);
